import type { Practitioner } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';
import { FHIR_USER_ID_SYSTEM } from '@eluve/fhir-utils';

import { Field } from '../../components/Field';
import type { FhirView } from '../../core/view-contract';
import { formatHumanName } from '../accessors';
import { formatFhirDateTime } from '../observation/format';

/**
 * The exported Practitioner component. As a reference target (requester,
 * performer, asserter — i.e. under a parent) it renders inline; rendered at
 * the root (the member FHIR mirror card) it is the standalone read card.
 */
export const PractitionerView: FhirView<Practitioner> = (props) => {
  if (props.ctx.inList || props.ctx.parent !== undefined) {
    return <PractitionerInlineView {...props} />;
  }
  return <PractitionerReadView {...props} />;
};

const PractitionerInlineView: FhirView<Practitioner> = ({ data }) => {
  const qualification = data.qualification?.[0]?.code?.text;
  const name = formatHumanName(data.name?.[0]) || 'Practitioner';
  return (
    <Text
      inline
      size="sm"
      weight="regular"
      className="truncate"
      data-view="practitioner-inline"
    >
      {name}
      {qualification ? `, ${qualification}` : ''}
    </Text>
  );
};

/**
 * Standalone card: identity header plus the fact grid the eluve mirror
 * writes — contact details and the durable eluve identifier. Fields other
 * writers own (qualifications, foreign identifiers) surface where they
 * exist but are never required.
 */
const PractitionerReadView: FhirView<Practitioner> = ({ data, ctx }) => {
  const qualification = data.qualification?.[0]?.code?.text;
  return (
    <div
      data-view="practitioner-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="min-w-0"
        >
          <Text inline size="sm" weight="semibold" className="truncate">
            {formatHumanName(data.name?.[0]) || 'Practitioner'}
          </Text>
          {qualification && (
            <Text inline size="xs" color="tertiary">
              {qualification}
            </Text>
          )}
        </Stack>
        {data.active === false && (
          <Tag pill subtle size="xs" color="gray" label="Inactive" />
        )}
      </Stack>
      {/* Field: typed path drives extraction + policy identity + DAR + display —
          an absent value renders its honest state, never a bare em-dash. */}
      <Grid columns={1} className="mt-3 gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field
          label="Email"
          data={data}
          path={['telecom', { system: 'email' }, 'value']}
        />
        <Field
          label="Phone"
          data={data}
          path={['telecom', { system: 'phone' }, 'value']}
        />
        <Field
          label="Eluve user ID"
          data={data}
          path={['identifier', { system: FHIR_USER_ID_SYSTEM }, 'value']}
        />
      </Grid>
      {/* Not a Field: `meta` is resource plumbing, absent from the rendering
          manifest, and never subject to field-level withholding. */}
      {data.meta?.lastUpdated && (
        <Text size="xs" color="tertiary" className="mt-3">
          Last synced {formatFhirDateTime(data.meta.lastUpdated)}
        </Text>
      )}
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
